import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Returns the sorted, de-duplicated set of media ids currently rendered
   * inside a gallery element. Used to detect whether a variant change
   * actually changes the media set.
   *
   * @param {ParentNode} root
   * @returns {string}
   */
  #mediaSignature(root) {
    if (!root || !root.querySelectorAll) return '';
    const ids = Array.prototype.map.call(
      root.querySelectorAll('.product-media[data-media-id]'),
      (el) => el.getAttribute('data-media-id')
    );
    // De-duplicate (grid + slideshow render each media twice) and sort so
    // ordering differences don't count as a change.
    return Array.from(new Set(ids)).sort().join(',');
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * Corus customisation: when the incoming gallery contains exactly the same
   * set of media as the one already on the page (which is always the case on
   * this store, where colours are filtered client-side rather than tied to
   * variant-specific media), we SKIP the replacement entirely. Rebuilding the
   * gallery would destroy the already-loaded <img> elements and recreate them
   * empty, causing a white flash and a perceptible reload on every colour
   * change. Skipping keeps the loaded images in place; the colour filter
   * (corus-color-filter.js) performs the visual switch instantly. If a product
   * genuinely has different media per variant, the media sets differ and we
   * fall back to the original replace behaviour.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    try {
      const currentSignature = this.#mediaSignature(this);
      const newSignature = this.#mediaSignature(newMediaGallery);
      if (currentSignature && currentSignature === newSignature) {
        // Same media set -> no rebuild needed. Let the colour filter handle
        // the visual change against the already-loaded gallery.
        return;
      }
    } catch (error) {
      // If anything goes wrong detecting the signature, fall back to the
      // original (safe) behaviour below.
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
